import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '../Icon/Icon';
import Menu from '../Menu/Menu';
import Button from '../Button/Button';
import LoadingIndicator from './LoadingIndicator';
import { arrowKey, arrowUp, arrowDown, arrowRight, arrowLeft, enter, escape, tab } from '../../lib/keyEvents';
import createFakeEvent from '../../lib/createFakeEvent';

const simulateClickOutside = () => {
  const modal = document.querySelector('div .input-select-list-modal');

  if (modal) {
    modal.click();
  }
};

const onKeyDown = (onSelectionChanged, selection, selectionHandler) => e => {
  const activeElement = document.activeElement;
  if (arrowKey(e.key)) {
    e.preventDefault();
  }
  if (arrowUp(e.key)) {
    const previous = activeElement.previousSibling;
    if (previous) {
      previous.focus();
      const row = previous.getAttribute('data-row');
      const newSelection = selectionHandler.handleSelectionEvent(null, row, selection);
      onSelectionChanged(createFakeEvent(newSelection));
    }
  }
  if (arrowDown(e.key)) {
    const next = activeElement.nextSibling;
    if (next) {
      next.focus();
      const row = next.getAttribute('data-row');
      const newSelection = selectionHandler.handleSelectionEvent(null, row, selection);
      onSelectionChanged(createFakeEvent(newSelection));
    }
  }
  if (arrowRight(e.key) || arrowLeft(e.key)) {
    const firstGrandChild = activeElement.childNodes[0].childNodes[0];
    const isExpandItem = firstGrandChild.classList.contains('show-hide');

    if (isExpandItem) {
      firstGrandChild.click();
    }
  }
  if (enter(e.key)) {
    const firstChild = activeElement.childNodes[0];
    firstChild.click();
    simulateClickOutside();
  }

  if (escape(e.key)) {
    simulateClickOutside();
  }

  if (tab(e.key)) {
    e.preventDefault();
    simulateClickOutside();
  }
};

const handleClick = onClick => e => {
  if (onClick) {
    onClick(e);
  }
  simulateClickOutside();
};

const Node = ({
  columns,
  expanded,
  iconName,
  itemDisabledKey,
  itemRenderer,
  items,
  lazyLoadChildren,
  lazyLoadChildrenText,
  level,
  loadingText,
  nestedKey,
  onClick,
  onCloseMenu,
  onCreateMenuItems,
  onExpandCollapse,
  onLoadChildren,
  onSelectionChanged,
  parentIndex,
  selection,
  selectionHandler,
  width,
}) => {
  const style = columns.length === 1 ? { width } : {};
  return items.map((item, index) => {
    if (!item) {
      return null;
    }
    const rowKey = `${parentIndex}${index}`;
    const childNodes = item[nestedKey];
    const rowExpanded = expanded[rowKey];
    const hasChildNodes = rowExpanded && childNodes;
    const childNodesLoaded = hasChildNodes && childNodes.length > 0;
    const moreItems = lazyLoadChildren && item.hasMore;
    const moreItemsLoaded = moreItems && !item.isLoading;
    const nextLevel = level + 1;

    return <Fragment key={rowKey}>
      <tr
        key={rowKey}
        id={`node-index-${rowKey}`}
        data-row={rowKey}
        tabIndex="0"
        onKeyDown={onKeyDown(onSelectionChanged, selection, selectionHandler)}
        className={classNames(
          'tree-node',
          { 'selected': selection[rowKey] },
          { 'disabled': itemDisabledKey && !item[itemDisabledKey] }
        )}
      >
        {columns.length > 0 ? columns.map((column, columnIndex) => {
          const key = `${rowKey}.${columnIndex}`;
          const shouldRenderExpandArrow = columnIndex === 0 && childNodes && childNodes.length >= 0;

          return (
            <td
              key={key}
              data-key={key}
              className={classNames(
                { [`level-${level}`]: columnIndex === 0 },
                { 'expanded': rowExpanded },
                { 'no-arrow': columnIndex === 0 && !shouldRenderExpandArrow }
              )}
              onClick={handleClick(onClick)}
              style={style}
            >
              {shouldRenderExpandArrow ?
                <Icon name={iconName} className="show-hide" onClick={onExpandCollapse} /> :
                null
              }
              {itemRenderer(item, column, columnIndex, key)}
            </td>
          )
        }) : 'Loading...'}
        {onCreateMenuItems && <td key="menu" className="menu">
          <Menu
            className="cell-menu" buttonClass="core-icon-button"
            onCreateMenuItems={onCreateMenuItems} data={item}
            onClose={onCloseMenu}
          />
        </td>}
      </tr>
      {
        hasChildNodes &&
        <Fragment>
          {childNodesLoaded ?
            <Fragment>
              <Node
                columns={columns}
                expanded={expanded}
                iconName={iconName}
                itemDisabledKey={itemDisabledKey}
                itemRenderer={itemRenderer}
                items={childNodes}
                lazyLoadChildren={lazyLoadChildren}
                lazyLoadChildrenText={lazyLoadChildrenText}
                level={nextLevel}
                loadingText={loadingText}
                nestedKey={nestedKey}
                onClick={onClick}
                onCloseMenu={onCloseMenu}
                onCreateMenuItems={onCreateMenuItems}
                onExpandCollapse={onExpandCollapse}
                onLoadChildren={onLoadChildren}
                onSelectionChanged={onSelectionChanged}
                parentIndex={`${rowKey}.`}
                selection={selection}
                selectionHandler={selectionHandler}
                width={style.width}
              />
              {moreItemsLoaded ?
                <tr key={`lazy-load-${rowKey}`} className="lazy-load">
                  <td className={classNames(`level-${level}`)}>
                    <Button data-key={rowKey} onClick={onLoadChildren} type="button">
                      {lazyLoadChildrenText || 'More...'}
                    </Button>
                  </td>
                </tr> :
                <Fragment>
                  {item.isLoading ?
                    <LoadingIndicator rowKey={rowKey} level={nextLevel}>
                      {loadingText}
                    </LoadingIndicator> :
                    null}
                </Fragment>
              }
            </Fragment> :
            <LoadingIndicator rowKey={rowKey} level={nextLevel}>
              {loadingText}
            </LoadingIndicator>
          }
        </Fragment>
      }
    </Fragment >
  })
};

Node.propTypes = {
  columns: PropTypes.array,
  expanded: PropTypes.shape(),
  iconName: PropTypes.string,
  itemDisabledKey: PropTypes.string,
  itemRenderer: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        hasMore: PropTypes.bool,
        isLoading: PropTypes.bool
      }),
      PropTypes.string
    ]),
  ),
  lazyLoadChildren: PropTypes.bool,
  lazyLoadChildrenText: PropTypes.string,
  level: PropTypes.number,
  loadingText: PropTypes.string,
  nestedKey: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  onCloseMenu: PropTypes.func,
  onCreateMenuItems: PropTypes.func,
  onExpandCollapse: PropTypes.func.isRequired,
  onLoadChildren: PropTypes.func.isRequired,
  parentIndex: PropTypes.string,
  selection: PropTypes.object,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

Node.defaultProps = {
  columns: [],
  items: [],
  level: 0,
  nestedKey: 'children',
  onCloseMenu: null,
  onCreateMenuItems: null,
  parentIndex: '',
};

export default Node;
